import {
  AppShell,
  Button,
  Group,
  Modal,
  Skeleton,
  Table,
  Loader,
  Box,
} from "@mantine/core";

import { JSX, useEffect, useMemo, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { useLocation } from "wouter";
import {
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { TextSearchBox } from "../shared/components/TextSearchBox";
import {
  DeleteActivityRequest,
  useGetVentQuery,
  useDeleteActivityMutation,
} from "../slices/api/ventsApi";
import { openConfirmationModal } from "../shared/components/Notifications";
import CreateActivity from "./CreateActivity";
import { useGetAssignmentsQuery } from "../slices/api/assignmentsApi";
import { Assignments, GetTotalForActivity } from "../slices/types";
import ActivityAssignments from "./ActivityAssignments";
import {
  IconAddNew,
  IconDelete,
  IconEdit,
  IconGuests,
} from "../shared/components/Icons";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { setConsoleIds } from "./Console";
import { TabHeader } from "../shared/components/TabHeader";
import { formatFullName } from "../shared/utility";
import {
  GetGuestsResponseRecord,
  useGetGuestsQuery,
} from "../slices/api/guestsApi";

interface Props {
  ventId: string;
  setEventTabDescription: (name: string) => void;
}

function ActivitiesTab(props: Props) {
  const ventId = props.ventId;
  const [_, navigate] = useLocation();

  const [opened, { open, close }] = useDisclosure(false);
  const [deleteActivity] = useDeleteActivityMutation();
  const {
    data: ventResponse,
    isFetching: isFetchingVent,
    isSuccess: isSuccessVent,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({
    id: ventId,
  });
  const {
    data: assignmentsResponse,
    isFetching: isFetchingAssignments,
    isSuccess: isSuccessAssignments,
    isError: isErrorFetchingAssignments,
    error: assignmentsError,
  } = useGetAssignmentsQuery({
    ventId: ventId,
  });

  const {
    data: getGuestsResponse,
    isFetching: isFetchingGuests,
    isSuccess: isSuccessGuests,
    isError: isErrorFetchingGuests,
    error: guestsError,
  } = useGetGuestsQuery({
    ventId: ventId,
  });

  const guestMap = useMemo((): Record<string, GetGuestsResponseRecord> => {
    if (getGuestsResponse?.guests == null) return {};
    return getGuestsResponse.guests.reduce(
      (acc, getGuestsResponseRecord) => {
        acc[getGuestsResponseRecord.id] = getGuestsResponseRecord;
        return acc;
      },
      {} as Record<string, GetGuestsResponseRecord>
    );
  }, [getGuestsResponse]);

  useEffect(() => {
    if (ventResponse) {
      props.setEventTabDescription(ventResponse.name);
    }
  }, [ventResponse]);
  const [searchText, setSearchText] = useState("");

  const [
    openedActivityAssignments,
    { open: openActivityAssignments, close: closeActivityAssignments },
  ] = useDisclosure(false);
  const [currentActivityId, setCurrentActivityId] = useState("");

  const handleDelete = async (activityId: string) => {
    if (ventResponse === undefined) {
      console.error("vent is undefined but should not be");
      return;
    }

    const request: DeleteActivityRequest = {
      activityId: activityId,
      ventId,
      updateTime: ventResponse.updateTime!,
    };

    try {
      await deleteActivity(request).unwrap();
      setConsoleIds({ activityId: null });
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (
    isErrorFetchingAssignments ||
    isErrorFetchingVent ||
    isErrorFetchingGuests
  ) {
    handleFetchingError(assignmentsError ?? ventError ?? guestsError);
    return null;
  }

  if (!ventResponse || !assignmentsResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  let rows: JSX.Element[] = [];
  if (
    isSuccessVent &&
    isSuccessAssignments &&
    isSuccessGuests &&
    ventResponse != null &&
    Object.keys(ventResponse.activities ?? {}).length > 0
  ) {
    const activities = Object.entries(ventResponse.activities).map((v) => {
      return { id: v[0], ...v[1] };
    });
    const filteredActivites = activities.filter((activity) =>
      activity.description!.toLowerCase().includes(searchText.toLowerCase())
    );
    const sorted = filteredActivites.sort((a, b) =>
      (a.description ?? "") < (b.description ?? "") ? -1 : 1
    );
    rows = sorted.map((activity) => {
      const activityTotal = GetTotalForActivity(
        assignmentsResponse?.assignments,
        activity.id
      );
      const assignmentText = getAssignmentsForActivityText(
        activity.id,
        assignmentsResponse?.assignments,
        guestMap
      );
      return (
        <Table.Tr key={activity.id}>
          <Table.Td>
            <Group gap="0.5rem">
              <EbActionIcon
                disabled={!ventResponse.canUserEdit}
                aria-label="Edit activity"
                onClick={() => {
                  if (!ventResponse.canUserEdit) return;
                  navigate(
                    `/console/eventactivity/event/${ventId}/activity/${activity.id}`
                  );
                }}
              >
                <EbTooltip label="Edit activity">
                  <IconEdit />
                </EbTooltip>
              </EbActionIcon>
              <EbActionIcon
                disabled={!ventResponse.canUserEdit}
                aria-label="Delete activity"
                onClick={() => {
                  if (!ventResponse.canUserEdit) return;
                  openConfirmationModal({
                    message: `Please confirm deletion of the activity ${activity.description}`,
                    title: "Delete Activity",
                    onConfirm: () => handleDelete(activity.id),
                  });
                }}
              >
                <EbTooltip label="Delete activity">
                  <IconDelete />
                </EbTooltip>
              </EbActionIcon>
              {ventResponse.canUserViewGuests && (
                <EbActionIcon
                  style={{
                    display: activityTotal > 0 ? "block" : "none",
                  }}
                  disabled={!ventResponse.canUserViewGuests}
                  aria-label="See who is assigned"
                  onClick={() => {
                    if (!ventResponse.canUserViewGuests) return;
                    setCurrentActivityId(activity.id);
                    openActivityAssignments();
                  }}
                >
                  <EbTooltip label="See who is assigned">
                    <IconGuests />
                  </EbTooltip>
                </EbActionIcon>
              )}
            </Group>
          </Table.Td>
          <Table.Td>{activity.description}</Table.Td>
          <Table.Td>
            <EbTooltip label={assignmentText}>
              <Box
                style={{
                  maxWidth: 125,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {assignmentText}
              </Box>
            </EbTooltip>
          </Table.Td>
          {activity.limitPerEvent > 0 && (
            <Table.Td>
              {activity.limitPerEvent - activityTotal} of{" "}
              {activity.limitPerEvent}
            </Table.Td>
          )}
          {activity.limitPerEvent === 0 && (
            <Table.Td>Have {activityTotal}</Table.Td>
          )}
          <Table.Td>{activity.limitPerGuest}</Table.Td>
          <Table.Td>{activity.hostManaged ? "Yes" : "No"}</Table.Td>
        </Table.Tr>
      );
    });
  }
  return (
    <AppShell.Main>
      <Modal opened={opened} onClose={close} withCloseButton={false} centered>
        <CreateActivity
          title="Add Activity"
          onClose={close}
          ventId={ventId}
          updateTime={ventResponse?.updateTime!}
        />
      </Modal>
      <Modal
        size="auto"
        opened={openedActivityAssignments}
        onClose={closeActivityAssignments}
        withCloseButton={false}
        centered
      >
        <ActivityAssignments
          ventId={ventId}
          activityId={currentActivityId}
          onClose={closeActivityAssignments}
        />
      </Modal>
      <TabHeader
        label="Activities for"
        ventId={ventId}
        ventName={ventResponse.name}
      />
      <Group>
        <Button
          disabled={!ventResponse.canUserEdit}
          onClick={open}
          rightSection={<IconAddNew />}
        >
          Add Activity
        </Button>
      </Group>
      <TextSearchBox setSearchText={setSearchText} searchText={searchText} />
      <Skeleton
        visible={isFetchingVent || isFetchingAssignments || isFetchingGuests}
      >
        <div hidden={!isSuccessVent || rows.length === 0}>
          <Table.ScrollContainer minWidth={500}>
            <Table stickyHeader stickyHeaderOffset={0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Actions</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Assigned</Table.Th>
                  <Table.Th>Remaining</Table.Th>
                  <Table.Th>Limit per guest</Table.Th>
                  <Table.Th>Host Managed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </div>
        <div
          hidden={
            isFetchingVent ||
            isFetchingAssignments ||
            isFetchingGuests ||
            rows.length > 0
          }
        >
          No activities displayed. Change search or add a activity.
        </div>
      </Skeleton>
    </AppShell.Main>
  );
}

const getAssignmentsForActivityText = (
  activityId: string,
  assignments: Assignments,
  guestMap: Record<string, GetGuestsResponseRecord>
): string => {
  // Get the guestIds that have assignments for the activity
  const assignedGuests: string[] = [];
  Object.keys(assignments).forEach((guestId) => {
    const guestAssignments = assignments[guestId];
    const activityAssignment = guestAssignments[activityId];
    if (activityAssignment) {
      const guest = guestMap[guestId];
      const name = formatFullName(guest.firstName, guest.lastName);
      assignedGuests.push(
        name +
          (activityAssignment.quantity > 1
            ? `(${activityAssignment.quantity})`
            : "")
      );
    }
  });
  return assignedGuests.join(", ");
};

export default ActivitiesTab;
